/**
 * Synthesised sound effects — Web Audio API only, no audio files.
 *
 * Everything routes through `master`, which feeds both the speakers and a
 * MediaStreamDestination so screen recordings contain the exact same audio.
 */

let ctx = null;
let master = null;
let recordDestination = null;
let noiseBuffer = null;
let muted = false;
let lastTickAt = 0;
let tickCount = 0;

const MASTER_VOLUME = 0.7;

function context() {
  if (ctx) return ctx;
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return null;
  ctx = new AudioCtx({ latencyHint: 'interactive' });

  master = ctx.createGain();
  master.gain.value = muted ? 0 : MASTER_VOLUME;

  // Gentle limiter so fanfare chords never clip.
  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = -10;
  limiter.knee.value = 8;
  limiter.ratio.value = 6;
  limiter.attack.value = 0.003;
  limiter.release.value = 0.2;

  master.connect(limiter);
  limiter.connect(ctx.destination);
  recordDestination = ctx.createMediaStreamDestination();
  limiter.connect(recordDestination);

  noiseBuffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.06), ctx.sampleRate);
  const data = noiseBuffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  return ctx;
}

/** Must be called from a user gesture (click/keydown) before the first sound. */
export function unlockAudio() {
  const c = context();
  if (c && c.state === 'suspended') c.resume().catch(() => {});
}

export function setMuted(value) {
  muted = Boolean(value);
  if (ctx && master) master.gain.setTargetAtTime(muted ? 0 : MASTER_VOLUME, ctx.currentTime, 0.02);
}

/** Audio track carrying all SFX, for MediaRecorder. */
export function recordingAudioTrack() {
  context();
  return recordDestination?.stream.getAudioTracks()[0] ?? null;
}

function envelope(param, t, peak, attack, decay) {
  param.cancelScheduledValues(t);
  param.setValueAtTime(0.0001, t);
  param.exponentialRampToValueAtTime(peak, t + attack);
  param.exponentialRampToValueAtTime(0.0001, t + attack + decay);
}

/**
 * Mechanical tick/tock — a filtered noise click plus a tiny pitched "body".
 * Alternates pitch like a ratchet; throttled so very fast spins stay clean.
 */
export function tick() {
  const c = context();
  if (!c || c.state !== 'running') return;
  const now = c.currentTime;
  if (now - lastTickAt < 0.028) return;
  lastTickAt = now;
  const tock = tickCount++ % 2 === 1;

  const noise = c.createBufferSource();
  noise.buffer = noiseBuffer;
  const band = c.createBiquadFilter();
  band.type = 'bandpass';
  band.frequency.value = tock ? 1900 : 3200;
  band.Q.value = 6;
  const noiseGain = c.createGain();
  envelope(noiseGain.gain, now, 0.55, 0.0015, 0.04);
  noise.connect(band).connect(noiseGain).connect(master);
  noise.start(now);
  noise.stop(now + 0.06);

  const body = c.createOscillator();
  body.type = 'square';
  body.frequency.setValueAtTime(tock ? 820 : 1250, now);
  body.frequency.exponentialRampToValueAtTime(tock ? 260 : 420, now + 0.03);
  const bodyGain = c.createGain();
  envelope(bodyGain.gain, now, 0.07, 0.001, 0.03);
  body.connect(bodyGain).connect(master);
  body.start(now);
  body.stop(now + 0.05);
}

/** Countdown beep; `final` is the higher "go" beep. */
export function countdownBeep(final = false) {
  const c = context();
  if (!c) return;
  const now = c.currentTime;
  const osc = c.createOscillator();
  osc.type = 'sine';
  osc.frequency.value = final ? 1318.5 : 880;
  const gain = c.createGain();
  envelope(gain.gain, now, 0.35, 0.005, final ? 0.6 : 0.25);
  osc.connect(gain).connect(master);
  osc.start(now);
  osc.stop(now + 0.7);
}

function brassVoice(c, freq, start, duration, peak) {
  const out = c.createGain();
  out.gain.setValueAtTime(0.0001, start);
  out.gain.exponentialRampToValueAtTime(peak, start + 0.03);
  out.gain.setTargetAtTime(peak * 0.7, start + 0.08, 0.1);
  out.gain.setTargetAtTime(0.0001, start + duration, 0.12);

  const filter = c.createBiquadFilter();
  filter.type = 'lowpass';
  filter.Q.value = 1.2;
  filter.frequency.setValueAtTime(900, start);
  filter.frequency.exponentialRampToValueAtTime(3600, start + 0.06);
  filter.frequency.setTargetAtTime(2200, start + 0.1, 0.2);

  const vibrato = c.createOscillator();
  vibrato.frequency.value = 5.5;
  const vibratoDepth = c.createGain();
  vibratoDepth.gain.setValueAtTime(0, start);
  vibratoDepth.gain.linearRampToValueAtTime(freq * 0.006, start + 0.35);
  vibrato.connect(vibratoDepth);

  for (const [type, detune, level] of [
    ['sawtooth', -6, 0.5],
    ['sawtooth', 6, 0.5],
    ['triangle', 0, 0.6],
  ]) {
    const osc = c.createOscillator();
    osc.type = type;
    osc.frequency.value = freq;
    osc.detune.value = detune;
    vibratoDepth.connect(osc.frequency);
    const g = c.createGain();
    g.gain.value = level;
    osc.connect(g).connect(filter);
    osc.start(start);
    osc.stop(start + duration + 0.8);
  }
  vibrato.start(start);
  vibrato.stop(start + duration + 0.8);
  filter.connect(out).connect(master);
}

/** Celebratory fanfare: rising C-major arpeggio into a held chord, with sparkle and a soft boom. */
export function fanfare() {
  const c = context();
  if (!c) return;
  const t = c.currentTime + 0.03;
  const C5 = 523.25;
  const E5 = 659.25;
  const G5 = 783.99;
  const C6 = 1046.5;

  // Pickup arpeggio: ta-ta-ta-TAAA
  [C5, E5, G5].forEach((f, i) => brassVoice(c, f, t + i * 0.11, 0.09, 0.16));
  brassVoice(c, C6, t + 0.33, 0.22, 0.2);
  // Held chord
  [C5, E5, G5, C6].forEach((f) => brassVoice(c, f, t + 0.6, 1.3, 0.12));
  brassVoice(c, C5 / 2, t + 0.6, 1.3, 0.1);

  // Boom on the chord
  const boom = c.createOscillator();
  boom.type = 'sine';
  boom.frequency.setValueAtTime(140, t + 0.6);
  boom.frequency.exponentialRampToValueAtTime(42, t + 1.1);
  const boomGain = c.createGain();
  envelope(boomGain.gain, t + 0.6, 0.5, 0.01, 0.6);
  boom.connect(boomGain).connect(master);
  boom.start(t + 0.6);
  boom.stop(t + 1.4);

  // Sparkles
  for (let i = 0; i < 9; i++) {
    const s = c.createOscillator();
    s.type = 'sine';
    s.frequency.value = [2093, 2637, 3136, 4186][i % 4] * (1 + (Math.random() - 0.5) * 0.01);
    const g = c.createGain();
    const at = t + 0.62 + i * 0.09 + Math.random() * 0.04;
    envelope(g.gain, at, 0.05, 0.004, 0.35);
    s.connect(g).connect(master);
    s.start(at);
    s.stop(at + 0.45);
  }
}
